import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { FormSchema } from '@hcc/types';
import { conditionMet } from '@hcc/types';
import { api, ApiError } from '../../lib/api';
import { Button } from '../ui/Button';
import { FieldView, type FieldInvalid } from './formfields/FieldView';
import {
  validateFormValues,
  isFieldVisible,
  schemaNeedsConsent,
  type ValidateFormResult,
} from './formfields/validateForm';
import { type PendingFile } from './FileUploadField';
import { FormFileUploadField } from './FormFileUploadField';
import { SignatureField } from './formfields/SignatureField';
import { DamageMarkupField } from './formfields/DamageMarkupField';
import { asDamageMarkup } from '@hcc/types';
import { useFormDraft } from './useFormDraft';

interface FormMeta {
  version_id: string;
  version: number;
  title: string;
  description: string | null;
  schema: FormSchema;
}

interface FormRendererProps {
  taskId: string;
  formId: string;
  onDone: () => void;
}

/** Renders an assigned form from its published schema and submits it. */
export function FormRenderer({ taskId, formId, onDone }: FormRendererProps) {
  const qc = useQueryClient();
  const draft = useFormDraft(taskId);
  const { values, setValues, consent, setConsent, step, setStep, savedFiles, setSavedFiles, priorFiles } = draft;
  // Signature captures, base64 until submit. File_upload fields persist to the
  // server as they're picked and live in `savedFiles` instead.
  const [files, setFiles] = useState<Record<string, PendingFile[]>>({});
  // Validation stays silent until the first submit attempt; after that the
  // error summary re-derives live so fixing a field removes its line at once.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Per-field validation state so incomplete fields paint red, not just the
  // text summary. Cleared for a field as soon as the inductee edits it.
  const [invalid, setInvalid] = useState<Record<string, FieldInvalid>>({});
  const [consentInvalid, setConsentInvalid] = useState(false);
  // Field keys with an upload in flight. Submit must wait for them: the
  // server clears the draft (and any pending video record) on submit, so a
  // submission mid-upload would cancel the upload and lose the file.
  const [busyUploads, setBusyUploads] = useState<Record<string, boolean>>({});
  const uploadsBusy = Object.values(busyUploads).some(Boolean);

  const meta = useQuery({
    queryKey: ['onboarding-form-meta', formId],
    queryFn: () => api<FormMeta>(`/api/me/onboarding/form/${formId}/meta`),
  });

  const submit = useMutation({
    mutationFn: () => {
      const flatFiles = Object.entries(files).flatMap(([field_key, list]) =>
        list.map((f) => ({ field_key, filename: f.filename, base64: f.base64 })),
      );
      return api<{ ok: true }>(`/api/me/onboarding/forms/${taskId}/submit`, {
        method: 'POST',
        body: JSON.stringify({ form_id: formId, values, files: flatFiles, consent }),
      });
    },
    onSuccess: () => {
      draft.markSubmitted();
      toast.success('Form submitted');
      qc.invalidateQueries({ queryKey: ['me-view'] });
      qc.invalidateQueries({ queryKey: ['onboarding-form-resume', taskId] });
      onDone();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not submit'),
  });

  if (meta.isLoading || draft.isLoading || !draft.seeded) {
    return <p className="text-sm text-fg-muted">Loading form…</p>;
  }
  if (meta.isError || !meta.data) return <p className="text-sm text-danger">Could not load this form.</p>;

  const schema = meta.data.schema;
  const pages = schema.pages.length ? schema.pages : [{ fields: [] }];
  // Conditionally hidden pages drop out of the flow entirely. Work in terms of
  // the visible pages only, snapping the saved step onto one if it now points
  // at a hidden page (e.g. an earlier answer changed).
  const visibleIdx = pages.map((_, i) => i).filter((i) => conditionMet(pages[i]!.visible_if, values));
  const flow = visibleIdx.length ? visibleIdx : [0];
  const clamped = Math.min(step, pages.length - 1);
  const curIdx = flow.includes(clamped)
    ? clamped
    : flow.find((i) => i >= clamped) ?? flow[flow.length - 1]!;
  const pos = flow.indexOf(curIdx);
  const page = pages[curIdx]!;
  const isLast = pos === flow.length - 1;
  const needsConsent = schemaNeedsConsent(schema);

  function validate(): ValidateFormResult {
    return validateFormValues({
      schema,
      values,
      signatureFiles: files,
      savedFiles,
      priorFiles,
      consent,
    });
  }

  function onSubmit() {
    const result = validate();
    setSubmitAttempted(true);
    setInvalid(result.invalid);
    setConsentInvalid(result.consentInvalid);
    if (result.messages.length === 0) submit.mutate();
  }

  // Recomputed every render once a submit has been attempted, so the summary
  // box tracks the current answers instead of the snapshot from the last
  // Submit click. Cheap: a single pass over the schema.
  const errors = submitAttempted ? validate().messages : [];

  // Drop a field's red highlight the moment the inductee starts fixing it.
  function clearInvalid(key: string) {
    setInvalid((m) => {
      if (!m[key]) return m;
      const next = { ...m };
      delete next[key];
      return next;
    });
  }

  return (
    <div className="mt-4 space-y-4">
      {flow.length > 1 ? (
        <div className="flex flex-wrap gap-1.5">
          {flow.map((i, n) => (
            <button key={i} type="button" onClick={() => setStep(i)}
              className={'rounded-full px-2.5 py-1 text-xs ' + (i === curIdx ? 'bg-accent text-fg-on-accent' : 'bg-surface-2 text-fg-muted')}>
              {n + 1}. {pages[i]!.title || `Page ${n + 1}`}
            </button>
          ))}
        </div>
      ) : null}

      {page.description ? <p className="text-sm text-fg-muted">{page.description}</p> : null}

      <div className="space-y-4">
        {page.fields.filter((f) => isFieldVisible(f, values)).map((f) =>
          f.type === 'signature' ? (
            <div key={f.key} className="space-y-1">
              <span className="block text-sm font-medium text-fg-muted">
                {f.label}
                {f.required ? <span className="text-danger"> *</span> : null}
              </span>
              {f.help ? <span className="block text-xs text-fg-dim">{f.help}</span> : null}
              <div className={invalid[f.key]?.whole ? 'rounded-md border border-danger/50 bg-danger-soft/10 p-1.5' : ''}>
                <SignatureField
                  hasPrior={(priorFiles[f.key]?.length ?? 0) > 0}
                  onChange={(dataUrl) => {
                    setFiles((s) => ({
                      ...s,
                      [f.key]: dataUrl ? [{ filename: `${f.key}.png`, base64: dataUrl }] : [],
                    }));
                    if (dataUrl) clearInvalid(f.key);
                  }}
                />
              </div>
            </div>
          ) : f.type === 'damage_markup' ? (
            <div key={f.key} className="space-y-1">
              <span className="block text-sm font-medium text-fg-muted">
                {f.label}
                {f.required ? <span className="text-danger"> *</span> : null}
              </span>
              {f.help ? <span className="block text-xs text-fg-dim">{f.help}</span> : null}
              <div className={invalid[f.key]?.whole ? 'rounded-md border border-danger/50 bg-danger-soft/10 p-1.5' : ''}>
                <DamageMarkupField
                  imageKey={f.image_key ?? ''}
                  value={asDamageMarkup(values[f.key])}
                  onChange={(v) => {
                    setValues((s) => ({ ...s, [f.key]: v }));
                  }}
                  onPng={(png) => {
                    // The flattened PNG rides the same pending-files channel
                    // as signatures and is stored on submit.
                    setFiles((s) => ({
                      ...s,
                      [f.key]: png ? [{ filename: `${f.key}.png`, base64: png }] : [],
                    }));
                    if (png) clearInvalid(f.key);
                  }}
                />
              </div>
            </div>
          ) : f.type === 'file_upload' ? (
            <div key={f.key} className={`space-y-1 ${invalid[f.key]?.whole ? 'rounded-md border border-danger/50 bg-danger-soft/10 p-2.5' : ''}`}>
              <FormFileUploadField
                taskId={taskId}
                field={f}
                files={savedFiles[f.key] ?? []}
                priorNames={priorFiles[f.key] ?? []}
                onUploaded={(saved) => {
                  setSavedFiles((s) => ({ ...s, [f.key]: [...(s[f.key] ?? []), saved] }));
                  clearInvalid(f.key);
                }}
                onDeleted={(key) =>
                  setSavedFiles((s) => ({ ...s, [f.key]: (s[f.key] ?? []).filter((x) => x.key !== key) }))
                }
                onBusyChange={(busy) => setBusyUploads((s) => ({ ...s, [f.key]: busy }))}
              />
            </div>
          ) : (
            <FieldView key={f.key} field={f} value={values[f.key] ?? null} invalid={invalid[f.key]}
              onChange={(v) => { setValues((s) => ({ ...s, [f.key]: v })); clearInvalid(f.key); }} />
          ),
        )}
      </div>

      {isLast && needsConsent ? (
        <label
          className={`flex items-start gap-2 rounded-token-sm border p-3 text-sm ${
            consentInvalid
              ? 'border-danger/60 bg-danger-soft/15 text-danger-soft-fg'
              : 'border-warning-soft/30 bg-warning-soft/15 text-warning-soft-fg'
          }`}
        >
          <input type="checkbox" checked={consent}
            onChange={(e) => { setConsent(e.target.checked); if (e.target.checked) setConsentInvalid(false); }}
            className="mt-0.5" />
          <span>{schema.consent?.statement ?? 'I consent to the processing of this data.'}</span>
        </label>
      ) : null}

      {errors.length > 0 ? (
        <ul className="list-disc space-y-0.5 rounded-token-sm border border-danger-soft/30 bg-danger-soft/15 p-3 pl-7 text-sm text-danger-soft-fg">
          {errors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      ) : null}

      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" disabled={pos === 0} onClick={() => setStep(flow[Math.max(pos - 1, 0)]!)}>
          Back
        </Button>
        {isLast ? (
          <Button
            variant="primary"
            size="sm"
            loading={submit.isPending}
            disabled={uploadsBusy}
            onClick={onSubmit}
          >
            Submit
          </Button>
        ) : (
          <Button variant="primary" size="sm" onClick={() => setStep(flow[Math.min(pos + 1, flow.length - 1)]!)}>
            Next
          </Button>
        )}
      </div>

      {isLast && uploadsBusy ? (
        <p className="text-center text-xs text-fg-dim">
          A file is still uploading. Submit unlocks when it finishes.
        </p>
      ) : null}

      <p className="text-center text-xs text-fg-dim">
        {draft.saveDraft.isPending
          ? 'Saving…'
          : draft.saveDraft.isError
          ? "Couldn't save your latest change — check your connection; it'll retry as you type."
          : 'Saved automatically — you can close this and finish later, even on another device.'}
      </p>
    </div>
  );
}
